import { Button } from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { useLocaledAmount, useMessage } from '@/providers';
import { EMPTY_DATA_ERROR } from '@/utils';
import { logger } from '@/utils';
import { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type { BatchModel } from '../../../_models/batch';
import { downloadBatchCsv } from '../../../_utils/download-csv';

type Props = {
  batch: z.infer<typeof BatchModel>;
  isLoading: boolean;
  jsonData: any;
};

export const DownloadCsv: FC<Props> = ({ batch, isLoading, jsonData }) => {
  const { t } = useTranslation();
  const { devtoolState } = useDevToolsStorage();
  const [isDownloading, setIsDownloading] = useState(false);
  const { unit } = useLocaledAmount();
  const { openMessage } = useMessage();
  const isDebugMode = !!devtoolState?.isDebugMode;

  const onExportFile = useCallback(async () => {
    setIsDownloading(true);
    try {
      downloadBatchCsv({
        jsonData,
        target: batch.target,
        batchParams: batch.params,
        unit,
        dateCreated: batch.taskResult.dateCreated,
        isDebugMode,
      });
    } catch (e) {
      logger.error('batch.csv-file-export-error', e);
      // TODO: Make batch requests return an error, removing handling here when doing so.
      if (e instanceof Error && e.message === EMPTY_DATA_ERROR) {
        openMessage('error', t('error-message-csv-export-empty-data'));
        return;
      }
      openMessage('error', t('error-message-file-download-failed'));
    } finally {
      setIsDownloading(false);
    }
  }, [batch, jsonData, t, unit, isDebugMode, openMessage]);

  return (
    <Button
      disabled={
        batch.status !== 'success' ||
        !batch.canDownloadFile ||
        batch.isDownloadFileOutdated ||
        isLoading
      }
      onClick={onExportFile}
      loading={isDownloading}
      type="primary"
    >
      {t('export-file')}
    </Button>
  );
};
